/**
 * OrphanFormPreviewModal.tsx
 *
 * Shows the full content of an orphaned (or soft-deleted) survey response so a
 * user can verify it before linking it to the patient. Reuses the read-only
 * renderer from SurveyResponseModal (SurveyResponseBody) and adds an
 * Attach/Restore action in the footer.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui';
import {
  getSurveyResponseDetail,
  attachOrphanForm,
  type OrphanFormCandidate,
  type AttachOrphanResult,
} from '@/services/patientForms.service';
import { SurveyResponseBody } from './SurveyResponseModal';

interface OrphanFormPreviewModalProps {
  patientId: number;
  candidate: OrphanFormCandidate | null;
  open: boolean;
  onClose: () => void;
  /** Called after a successful attach/restore so the parent can refresh + show feedback. */
  onAttached: (result: AttachOrphanResult, candidate: OrphanFormCandidate) => void;
}

export function OrphanFormPreviewModal({ patientId, candidate, open, onClose, onAttached }: OrphanFormPreviewModalProps) {
  const responseId = candidate?.surveysResponseId ?? null;
  const isDeleted = candidate?.category === 'deleted';
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open, responseId]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['orphan-form-preview', patientId, responseId],
    queryFn: () => getSurveyResponseDetail(patientId, responseId!, true),
    enabled: open && responseId !== null,
  });

  const attachMutation = useMutation({
    mutationFn: () => attachOrphanForm(patientId, responseId!),
    onSuccess: (result) => {
      if (candidate) onAttached(result, candidate);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to link the form.');
    },
  });

  const actionLabel = isDeleted ? 'Restore to this patient' : 'Attach to this patient';
  const confirmMsg = isDeleted
    ? 'Restore this deleted form to the patient?'
    : 'This action cannot be undone, so please be certain this is the correct patient before attaching this form.';

  const handleAttach = () => {
    if (!responseId) return;
    if (!window.confirm(confirmMsg)) return;
    attachMutation.mutate();
  };

  const footer = (
    <div className="flex w-full items-center justify-end gap-3">
      {error && <span className="mr-auto text-xs text-red-600">{error}</span>}
      <Button variant="secondary" onClick={onClose} disabled={attachMutation.isPending}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleAttach} disabled={attachMutation.isPending || !data}>
        {attachMutation.isPending ? 'Linking…' : actionLabel}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={open}
      onClose={attachMutation.isPending ? () => {} : onClose}
      title={candidate?.surveyTitle ?? 'Form'}
      size="xxl"
      footer={footer}
    >
      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : isError || !data ? (
        <p className="py-6 text-center text-sm text-red-600">Failed to load the form content.</p>
      ) : (
        <SurveyResponseBody detail={data} responseId={responseId ?? 0} />
      )}
    </Modal>
  );
}
